import React, { useState, useEffect } from "react";
import { formatDate } from "../../../utilities/format";
import { OriginalPost } from "./OriginalPostTable";
import BasicButton from "../../common/components/BasicButton";
import Editor from "../../common/components/Editor";
import { useOmniUpdateOriginalPostMutation, useOmniSelectOriginalPostMutation } from "../../../lib/apollo/graphql/omni/omni-generated";

interface Props {
  post: OriginalPost;
  onSave?: (post: OriginalPost) => void;
  onDelete?: (postId: string) => void;
  onClose: () => void;
  onRefreshDetail?: () => void; // 상세 데이터 새로고침용
  onSchedulePublish?: (postId: string) => void;
}

const OriginalPostEditMode: React.FC<Props> = React.memo(({
  post,
  onSave,
  onDelete,
  onClose,
  onRefreshDetail,
  onSchedulePublish,
}) => {
  
  // updateOriginalPost mutation 훅
  const [updateOriginalPost, { loading: updateLoading }] = useOmniUpdateOriginalPostMutation();
  // selectOriginalPost mutation 훅
  const [selectOriginalPost, { loading: selectLoading }] = useOmniSelectOriginalPostMutation();
  const [formData, setFormData] = useState({
    title: post.title,
    status: post.status,
    tags: post.tags,
    memo: post.memo || "",
    content: post.content || "",
    sourceUrl: post.sourceUrl || "",
  });

  // post가 변경될 때 formData 업데이트
  useEffect(() => {
    setFormData({
      title: post.title,
      status: post.status,
      tags: post.tags,
      memo: post.memo || "",
      content: post.content || "",
      sourceUrl: post.sourceUrl || "",
    });
  }, [post]);


  const [tagInput, setTagInput] = useState("");

  const STATUS_CONFIG: Record<string, { color: string; label: string }> = {
    NEW: { color: "bg-blue-100 text-blue-800 border-blue-200", label: "신규" },
    SELECTED: { color: "bg-yellow-100 text-yellow-800 border-yellow-200", label: "발행예정" },
    DRAFT_REQUESTED: { color: "bg-green-100 text-green-800 border-green-200", label: "발행중" },
    ON_HOLD: { color: "bg-gray-100 text-gray-800 border-gray-200", label: "보류" },
    DRAFTED: { color: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "발행완료" },
    DRAFT_REQUEST_FAILED: { color: "bg-red-100 text-red-800 border-red-200", label: "발행실패" },
    DELETED: { color: "bg-red-100 text-red-800 border-red-200", label: "삭제됨" },
    REJECTED: { color: "bg-orange-100 text-orange-800 border-orange-200", label: "거부됨" },
  };

  const getStatusOptions = () => {
    // 발행실패 상태일 때는 발행예정을 선택할 수 없음
    if (post.status === "DRAFT_REQUEST_FAILED") {
      return [
        { value: "NEW", label: "신규", editable: true },
        { value: "ON_HOLD", label: "보류", editable: true },
        { value: "DRAFT_REQUESTED", label: "발행중", editable: false },
        { value: "DRAFTED", label: "발행완료", editable: false },
        { value: "DRAFT_REQUEST_FAILED", label: "발행실패", editable: false },
      ];
    }

    return [
      { value: "NEW", label: "신규", editable: true },
      { value: "SELECTED", label: "발행예정", editable: true },
      { value: "ON_HOLD", label: "보류", editable: true },
      { value: "DRAFT_REQUESTED", label: "발행중", editable: false },
      { value: "DRAFTED", label: "발행완료", editable: false },
      { value: "DRAFT_REQUEST_FAILED", label: "발행실패", editable: false },
    ];
  };


  const handleSave = async () => {
    if (!formData.title.trim()) {
      alert("제목을 입력해주세요.");
      return;
    }
    
    try {
      const updateInput = {
        originalPostId: post.id,
        title: formData.title.trim(),
        status: formData.status as any, // Cast to match API enum
        memo: formData.memo || undefined, // 빈 문자열이면 undefined로 전송
        tags: formData.tags.length > 0 ? formData.tags : undefined, // 빈 배열이면 undefined로 전송
        content: formData.content || undefined, // 빈 문자열이면 undefined로 전송
      };

      const result = await updateOriginalPost({
        variables: { input: updateInput }
      });
      
      if (result.data) {
        // 발행예정 상태로 변경한 경우 selectOriginalPost도 호출
        if (formData.status === "SELECTED") {
          try {
            await selectOriginalPost({
              variables: {
                input: {
                  originalPostId: post.id,
                }
              }
            });
          } catch (selectError) {
            console.error('Failed to select original post:', selectError);
            // selectOriginalPost 실패해도 update는 성공했으므로 계속 진행
          }
        }
        
        alert('원문이 성공적으로 수정되었습니다.');
        
        // 상세 데이터 새로고침
        onRefreshDetail?.();
        
        // 레거시 콜백도 호출 (호환성을 위해)
        const updatedPost: OriginalPost = {
          ...post,
          title: formData.title.trim(),
          tags: formData.tags,
          memo: formData.memo,
          // API 응답에서 받은 상태와 업데이트 시간 반영
          status: result.data.updateOriginalPost.status,
          updatedAt: result.data.updateOriginalPost.updatedAt,
        };
        onSave?.(updatedPost);
      }
    } catch (error) {
      console.error('Failed to update original post:', error);
      alert('원문 수정에 실패했습니다. 다시 시도해주세요.');
    }
  };

  const handlePublish = () => {
    if (!formData.title.trim()) {
      alert("제목을 입력해주세요.");
      return;
    }

    // 발행 요청
    onSchedulePublish?.(post.id);
  };

  const handleDelete = () => {
    if (window.confirm("정말로 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.")) {
      onDelete?.(post.id);
    }
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleTagInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  return (
    <div className="p-6">
      <div className="space-y-6">
        {/* 제목 */}
        <div className="flex">
          <label className="text-sm font-medium text-gray-700" style={{ width: '100px', minWidth: '100px', paddingTop: '8px' }}>
            제목 *
          </label>
          <div className="flex-1 bg-white rounded-lg border border-gray-300 p-4">
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full bg-transparent border-none outline-none text-sm text-gray-700"
              placeholder="제목을 입력하세요"
              required
            />
          </div>
        </div>

        {/* 상태 */}
        <div className="flex items-center">
          <label className="text-sm font-medium text-gray-700" style={{ width: '100px', minWidth: '100px' }}>상태</label>
          <select
            value={formData.status}
            onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as OriginalPost["status"] }))}
            className={`w-32 rounded-full px-3 py-1 text-sm font-semibold border ${STATUS_CONFIG[formData.status].color} focus:outline-none cursor-pointer text-center`}
          >
            {getStatusOptions().map(option => (
              <option
                key={option.value}
                value={option.value}
                disabled={!option.editable}
              >
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* 작성일시 */}
        <div className="flex items-center">
          <label className="text-sm font-medium text-gray-700" style={{ width: '100px', minWidth: '100px' }}>작성일시</label>
          <p className="text-sm text-gray-600">{formatDate(post.createdAt, "YYYY년 MM월 DD일 HH:mm")}</p>
        </div>

        {/* 수정일시 */}
        <div className="flex items-center">
          <label className="text-sm font-medium text-gray-700" style={{ width: '100px', minWidth: '100px' }}>수정일시</label>
          <p className="text-sm text-gray-600">{formatDate(post.updatedAt, "YYYY년 MM월 DD일 HH:mm")}</p>
        </div>

        {/* 최종 수정자 */}
        <div className="flex items-center">
          <label className="text-sm font-medium text-gray-700" style={{ width: '100px', minWidth: '100px' }}>최종 수정자</label>
          <p className="text-sm text-gray-600">{(post as any).updatedBy || "-"}</p>
        </div>

        {/* 원문출처 URL */}
        <div className="flex">
          <label className="text-sm font-medium text-gray-700" style={{ width: '100px', minWidth: '100px', paddingTop: '8px' }}>
            원문출처
          </label>
          <div className="flex-1 bg-gray-50 rounded-lg border border-gray-300 p-4">
            {formData.sourceUrl ? (
              <a
                href={formData.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full text-sm text-blue-600 hover:text-blue-800 underline break-all"
              >
                {formData.sourceUrl}
              </a>
            ) : (
              <span className="text-sm text-gray-400">-</span>
            )}
          </div>
        </div>

        {/* 태그 */}
        <div className="flex items-start">
          <label className="text-sm font-medium text-gray-700" style={{ width: '100px', minWidth: '100px', paddingTop: '8px' }}>태그</label>
          <div className="flex-1">
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleTagInputKeyPress}
                className="flex-1 rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
                placeholder="태그를 입력하고 Enter 또는 추가 버튼을 누르세요"
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="rounded bg-gray-500 px-4 py-2 text-sm text-white hover:bg-gray-600"
              >
                추가
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800 border border-blue-200"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              ))}
              {formData.tags.length === 0 && (
                <p className="text-sm text-gray-500">설정된 태그가 없습니다.</p>
              )}
            </div>
          </div>
        </div>

        {/* 메모 */}
        <div className="flex">
          <label className="text-sm font-medium text-gray-700" style={{ width: '100px', minWidth: '100px', paddingTop: '8px' }}>
            메모
          </label>
          <div className="flex-1 bg-white rounded-lg border border-gray-300 p-4">
            <textarea
              value={formData.memo}
              onChange={(e) => setFormData(prev => ({ ...prev, memo: e.target.value }))}
              rows={3}
              className="w-full bg-transparent border-none outline-none resize-none text-sm text-gray-700"
              placeholder="메모를 입력하세요"
            />
          </div>
        </div>

        {/* 내용 */}
        <div className="flex items-start">
          <label className="text-sm font-medium text-gray-700" style={{ width: '100px', minWidth: '100px', paddingTop: '8px' }}>내용</label>
          <div className="flex-1">
            <div 
              style={{ 
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                overflow: 'hidden',
                height: '400px',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div style={{ flex: 1, minHeight: 0 }}>
                <Editor 
                  value={formData.content}
                  onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
                  height="100%"
                  mediaFolder="original-post"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 하단 버튼 */}
      <div className="sticky bottom-0 flex justify-between border-t bg-white p-4 -mx-6 -mb-6 mt-8">
        <div className="flex gap-3">
          {/* 발행 버튼: 발행예정 또는 발행중 상태일 때 표시 */}
          {(post.status === "SELECTED" || post.status === "DRAFT_REQUESTED") && (
            <BasicButton
              text="원고 발행"
              variant="primary"
              className={post.status === "DRAFT_REQUESTED" ? "bg-green-500 hover:bg-green-600" : "bg-blue-500 hover:bg-blue-600"}
              onClick={() => {
                if (post.status === "DRAFT_REQUESTED") {
                  if (window.confirm("이미 발행 중인 원문입니다. 다시 발행을 요청하시겠습니까?")) {
                    handlePublish();
                  }
                } else {
                  handlePublish();
                }
              }}
            />
          )}
          <BasicButton
            text="삭제"
            variant="error"
            onClick={handleDelete}
          />
        </div>
        <div className="flex gap-3">
          <BasicButton
            text={updateLoading || selectLoading ? "저장 중..." : "저장"}
            variant="outlined"
            className={`${updateLoading || selectLoading ? 'bg-gray-400' : 'bg-black hover:bg-gray-800'} text-white border-black`}
            onClick={handleSave}
            disabled={updateLoading || selectLoading}
          />
          <BasicButton
            text="닫기"
            variant="outlined"
            onClick={onClose}
          />
        </div>
      </div>
    </div>
  );
});

OriginalPostEditMode.displayName = 'OriginalPostEditMode';

export default OriginalPostEditMode;